import { Link, useParams } from "react-router-dom";
import { Activity, Compass, LoaderCircle, Users } from "lucide-react";
import type { Clinic } from "@fysaro/domain";
import { usePublicClinic } from "./hooks/usePublicClinic";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/design-system/components/ui/tabs";
import { useTranslation } from "react-i18next";
import { SURFACE_STYLES } from "@/design-system/lib/surface-styles";
import { PublicClinicTeam } from "./PublicClinicTeam";
import { PublicClinicServices } from "./PublicClinicServices";
import { PublicClinicHeader } from "./PublicClinicHeader";
import { PublicClinicAIWidget } from "./PublicClinicAIWidget";
import { PublicClinicOverview } from "./PublicClinicOverview";
import { MobileAIWidgetDrawer } from "./MobileAIWidgetDrawer";
import { HelpPanel } from "@/shared/help/components/HelpPanel";
import { PublicSeo } from "./components/PublicSeo";
import { FRONTEND_ROUTES } from "@fysaro/shared";

function buildPublicClinicLocation(clinic: Clinic): string {
  return (
    clinic.formattedAddress ||
    [clinic.city, clinic.country].filter(Boolean).join(", ") ||
    ""
  );
}

export function PublicClinicPage() {
  const { t } = useTranslation(["clinic"]);
  // 1. Capturamos el slug de la URL (/p/clinica-dueno-web -> slug = "clinica-dueno-web")
  const { slug } = useParams<{ slug: string }>();

  // 🚀 Nueva llamada limpia, estructurada
  const { data: clinic, isLoading, isError } = usePublicClinic(slug || "");

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoaderCircle className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isError || !clinic) {
    return (
      <>
        <PublicSeo
          title={t("clinic:seo.public_not_found_title")}
          description={t("clinic:seo.public_not_found_description")}
          path={slug ? `/p/${slug}` : "/p"}
          noindex
        />
        <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-4 bg-background text-center">
          <h1 className="text-xl font-black text-destructive uppercase tracking-wider">
            {t("clinic:empty.not_found")}
          </h1>
          <p className="max-w-md text-sm text-muted-foreground">
            {t("clinic:seo.public_not_found_description")}
          </p>
          <Link
            to={FRONTEND_ROUTES.public.homeClinic}
            className="text-sm font-bold text-primary underline-offset-4 hover:underline"
          >
            {t("clinic:seo.back_to_home")}
          </Link>
        </div>
      </>
    );
  }

  const location = buildPublicClinicLocation(clinic);
  const seoTitle = t("clinic:seo.public_title", { name: clinic.name });
  const seoDescription = t("clinic:seo.public_description", {
    name: clinic.name,
    location: location || t("clinic:seo.location_fallback"),
  });

  return (
    <>
      <PublicSeo
        title={seoTitle}
        description={seoDescription}
        path={`/p/${clinic.slug}`}
        imageUrl={clinic.imageUrl}
      />
      <div className="flex min-h-screen flex-col bg-background text-foreground">
      <div className="relative flex min-h-0 flex-1 overflow-hidden">
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="relative mx-auto max-w-7xl space-y-8 px-4 py-8">
      <PublicClinicHeader clinic={clinic} />

      {/* REJILLA PRINCIPAL DE LA PÁGINA */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
        {/* 📋 COLUMNA IZQUIERDA: LAS PESTAÑAS (Ocupa 2 de 3 columnas) */}
        <div className="md:col-span-2">
          <Tabs defaultValue="overview" className="space-y-6">
            <div className={SURFACE_STYLES.tabsListWrap}>
          <TabsList className={SURFACE_STYLES.tabsListBar}>
              <TabsTrigger
                value="overview"
                className={SURFACE_STYLES.tabTriggerUnderline}
              >
                <Compass className="w-4 h-4 mr-2" />
                {t("clinic:tabs.overview")}
              </TabsTrigger>

              <TabsTrigger
                value="services"
                className={SURFACE_STYLES.tabTriggerUnderline}
              >
                <Activity className="w-4 h-4 mr-2" />
                {t("clinic:tabs.services")}
              </TabsTrigger>

              <TabsTrigger value="team" className={SURFACE_STYLES.tabTriggerUnderline}>
                <Users className="w-4 h-4 mr-2" />
                {t("clinic:tabs.team.name")}
              </TabsTrigger>
            </TabsList>
        </div>

            <div className="min-h-[400px]">
              <TabsContent
                value="overview"
                className="animate-in fade-in slide-in-from-bottom-2 duration-300 outline-none"
              >
                <PublicClinicOverview clinic={clinic} />
              </TabsContent>

              <TabsContent
                value="services"
                className="animate-in fade-in slide-in-from-bottom-2 duration-300 outline-none"
              >
                <PublicClinicServices clinic={clinic} />
              </TabsContent>

              <TabsContent
                value="team"
                className="animate-in fade-in slide-in-from-bottom-2 duration-300 outline-none"
              >
                <PublicClinicTeam clinic={clinic} />
              </TabsContent>
            </div>
          </Tabs>
        </div>

        {/* 🖥️ COLUMNA DERECHA: DESKTOP ONLY (Ocupa 1 de 3 columnas) */}
        <div className="hidden md:block md:sticky md:top-6">
          <PublicClinicAIWidget clinicId={clinic.id} />
        </div>
      </div>

      {/* 📱 MOBILE ONLY: BOTÓN FLOTANTE FIJO + DRAWER DESLIZABLE */}
      <div className="md:hidden">
        <MobileAIWidgetDrawer clinicId={clinic.id} />
      </div>
          </div>
        </div>
        <HelpPanel contextScope="PUBLIC" />
      </div>
    </div>
    </>
  );
}
